import type {
  CreateStoreInput,
  IStoreRepository,
  Store,
  UpdateStoreInput,
} from '../../../interfaces/src';
import type { TypedSupabaseClient } from '../client';
import type { Database } from '../database-contract';
import type { PostgrestQueryBuilder } from '@supabase/supabase-js';

type StoreRelations = Pick<
  Database['public']['Views'],
  'authorized_stores' | 'authorized_store_members'
> & Pick<Database['public']['Tables'], 'store_members'>;

type StoreQuery<Name extends keyof StoreRelations> = PostgrestQueryBuilder<
  Database['__InternalSupabase'],
  Database['public'],
  StoreRelations[Name],
  Name
>;

function fromUntyped<Name extends keyof StoreRelations>(
  client: TypedSupabaseClient,
  table: Name,
): StoreQuery<Name> {
  return client.from(table as never) as unknown as StoreQuery<Name>;
}

function mapStore(row: Record<string, unknown>): Store {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    city: (row['city'] as string | null) ?? null,
    address: (row['address'] as string | null) ?? null,
    phone: (row['phone'] as string | null) ?? null,
    isDefault: Boolean(row['is_default']),
    isActive: row['is_active'] !== false,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export class SupabaseStoreRepository implements IStoreRepository {
  constructor(private readonly supabase: TypedSupabaseClient) {}

  async list(): Promise<Store[]> {
    const { data, error } = await fromUntyped(
      this.supabase,
      'authorized_stores',
    )
      .select('*')
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(mapStore);
  }

  async getById(id: string): Promise<Store | null> {
    const row = await this.getAuthorizedRowById(id);
    return row ? mapStore(row) : null;
  }

  async create(input: CreateStoreInput): Promise<Store> {
    const stores = await this.list();
    const shouldBeDefault = stores.length === 0 || input.isDefault === true;
    if (shouldBeDefault && input.isActive === false) {
      throw new Error('default_store_must_be_active');
    }
    const code = input.code.trim();
    const id = await this.mutateStore('create', null, {
      code,
      name: input.name.trim(),
      city: cleanNullable(input.city),
      address: cleanNullable(input.address),
      phone: cleanNullable(input.phone),
      is_default: shouldBeDefault,
      is_active: input.isActive ?? true,
    });

    const row = await this.getAuthorizedRowById(id);
    if (!row) throw new Error('store_readback_failed');
    return mapStore(row);
  }

  async update(id: string, input: UpdateStoreInput): Promise<Store> {
    const current = await this.getById(id);
    if (!current) throw new Error('store_not_found');
    if (current.isDefault && input.isDefault === false) {
      throw new Error('default_store_required');
    }
    if (current.isDefault && input.isActive === false) {
      throw new Error('default_store_must_be_active');
    }
    if (
      input.isDefault === true &&
      (input.isActive === false ||
        (input.isActive === undefined && !current.isActive))
    ) {
      throw new Error('default_store_must_be_active');
    }
    const patch: Record<string, unknown> = {};
    if (input.code !== undefined) patch['code'] = input.code.trim();
    if (input.name !== undefined) patch['name'] = input.name.trim();
    if (input.city !== undefined) patch['city'] = cleanNullable(input.city);
    if (input.address !== undefined)
      patch['address'] = cleanNullable(input.address);
    if (input.phone !== undefined) patch['phone'] = cleanNullable(input.phone);
    if (input.isDefault !== undefined) patch['is_default'] = input.isDefault;
    if (input.isActive !== undefined) patch['is_active'] = input.isActive;

    await this.mutateStore('update', id, patch);

    const updated = await this.getById(id);
    if (!updated) throw new Error('store_readback_failed');
    return updated;
  }

  async delete(id: string): Promise<void> {
    const store = await this.getById(id);
    if (!store) return;
    if (store.isDefault) throw new Error('default_store_cannot_be_deleted');

    await this.mutateStore('delete', id, {});
  }

  async listMemberIds(storeId: string): Promise<string[]> {
    const { data, error } = await fromUntyped(
      this.supabase,
      'authorized_store_members',
    )
      .select('user_id')
      .eq('store_id', storeId);
    if (error) throw error;
    return ((data ?? []) as Array<{ user_id: string }>).map(
      (row) => row.user_id,
    );
  }

  async assignMembers(storeId: string, userIds: string[]): Promise<void> {
    const uniqueUserIds = [...new Set(userIds.filter(Boolean))];
    if (uniqueUserIds.length === 0) return;

    const storeRow = await this.getAuthorizedRowById(storeId);
    if (!storeRow) throw new Error('store_not_found');

    const existing = new Set(await this.listMemberIds(storeId));
    const missing = uniqueUserIds.filter((userId) => !existing.has(userId));
    if (missing.length === 0) return;

    const tenantId = storeRow['tenant_id'];
    if (typeof tenantId !== 'string') throw new Error('store_tenant_missing');
    const { error } = await fromUntyped(this.supabase, 'store_members').insert(
      missing.map((userId) => ({
        tenant_id: tenantId,
        store_id: storeId,
        user_id: userId,
        is_default: false,
      })),
    );
    if (error) throw error;
  }

  private async getAuthorizedRowById(
    id: string,
  ): Promise<Record<string, unknown> | null> {
    const { data, error } = await fromUntyped(
      this.supabase,
      'authorized_stores',
    )
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return (data as Record<string, unknown> | null) ?? null;
  }

  private async mutateStore(
    action: 'create' | 'update' | 'delete',
    id: string | null,
    payload: Record<string, unknown>,
  ): Promise<string> {
    const { data, error } = (await this.supabase.rpc(
      'mutate_store' as never,
      {
        p_action: action,
        p_store_id: id,
        p_payload: payload,
      } as never,
    )) as { data: string | null; error: Error | null };
    if (error) throw error;
    if (!data || (id && data !== id)) throw new Error('store_mutation_failed');
    return data;
  }
}

function cleanNullable(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed || null;
}
